import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setModeratorNav } from '../../../redux/actions/navigation';
import deviceIcon from '../../../img/moderator-devices-icon-active.svg';
import { filterDeviceItems } from './deviceSearch';

import './DeviceLicenses.scss';

const statusLabels = {
    NotIssued: 'Не выпущена',
    Unused: 'Ожидает активации',
    Activated: 'Активна',
    Revoked: 'Отозвана',
    Expired: 'Срок истёк'
};

const getAccessToken = () => (
    localStorage.getItem('save-autorize') === 'true'
        ? localStorage.getItem('token')
        : sessionStorage.getItem('token')
);

const request = async (path, options = {}) => {
    const response = await fetch(path, {
        ...options,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
            ...(options.headers || {})
        }
    });

    if (response.status === 204) {
        return null;
    }

    const body = await response.json().catch(() => null);
    if (!response.ok) {
        throw new Error(body?.message || 'Не удалось выполнить операцию.');
    }

    return body;
};

const formatDate = value => value
    ? new Intl.DateTimeFormat('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'short'
    }).format(new Date(value))
    : '—';

const isExpired = value => value && new Date(value).getTime() <= Date.now();

const DeviceLicenses = () => {
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [validForDays, setValidForDays] = useState(30);
    const [issuedLicense, setIssuedLicense] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pendingPlaceId, setPendingPlaceId] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setIsLoading(true);
        setError('');

        try {
            setItems(await request('/api/moderator/desktop-devices'));
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        dispatch(setModeratorNav('Devices'));
        sessionStorage.setItem('moderatorNav', 'Devices');
        load();
    }, [dispatch, load]);

    useEffect(() => {
        setIsCopied(false);
    }, [issuedLicense]);

    const visibleItems = useMemo(
        () => filterDeviceItems(items, searchQuery),
        [items, searchQuery]
    );

    const summary = useMemo(() => ({
        total: items.length,
        active: items.filter(item =>
            item.licenseStatus === 'Activated' &&
            !isExpired(item.leaseExpiresAtUtc)).length,
        waiting: items.filter(item => item.licenseStatus === 'Unused').length,
        requiresCheck: items.filter(item =>
            item.deviceId && isExpired(item.leaseExpiresAtUtc)).length,
        syncErrors: items.filter(item => item.failedSyncOperations > 0).length,
        unavailable: items.filter(item =>
            ['NotIssued', 'Revoked', 'Expired'].includes(item.licenseStatus)).length
    }), [items]);

    const summaryCards = [
        { value: summary.total, label: 'рабочих зон', tone: 'total' },
        { value: summary.active, label: 'активных устройств', tone: 'active' },
        { value: summary.waiting, label: 'ждут активации', tone: 'waiting' },
        { value: summary.requiresCheck, label: 'требуют подключения', tone: 'attention' },
        { value: summary.syncErrors, label: 'ошибок синхронизации', tone: 'danger' },
        { value: summary.unavailable, label: 'без лицензии', tone: 'muted' }
    ];

    const issue = async (item, replaceExisting) => {
        if (replaceExisting && !window.confirm(
            `Отозвать текущее устройство «${item.placeName}» и выпустить новый ключ?`)) {
            return;
        }

        setPendingPlaceId(item.placeId);
        setError('');

        try {
            const path = replaceExisting
                ? `/api/moderator/desktop-devices/places/${item.placeId}/replace-license`
                : '/api/moderator/desktop-devices/licenses';
            const result = await request(path, {
                method: 'POST',
                body: JSON.stringify({
                    placeId: item.placeId,
                    validForDays: Number(validForDays)
                })
            });
            setIssuedLicense({ ...result, placeName: item.placeName });
            await load();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingPlaceId(null);
        }
    };

    const revoke = async item => {
        const isDevice = Boolean(item.deviceId);
        if (!window.confirm(
            `Отозвать ${isDevice ? 'устройство' : 'ключ'} рабочей зоны «${item.placeName}»?`)) {
            return;
        }

        setPendingPlaceId(item.placeId);
        setError('');

        try {
            const path = isDevice
                ? `/api/moderator/desktop-devices/devices/${item.deviceId}/revoke`
                : `/api/moderator/desktop-devices/licenses/${item.licenseId}/revoke`;
            await request(path, { method: 'POST' });
            await load();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setPendingPlaceId(null);
        }
    };

    const copyActivationKey = async () => {
        try {
            await navigator.clipboard.writeText(issuedLicense.activationKey);
            setIsCopied(true);
        } catch {
            setError('Не удалось скопировать ключ. Выделите его и скопируйте вручную.');
        }
    };

    return (
        <section className="moderator-devices">
            <div className="moderator__title moderator-devices__page-title">Desktop V2</div>

            <div className="moderator-devices__header">
                <div className="moderator-devices__heading">
                    <div className="moderator-devices__heading-icon" aria-hidden="true">
                        <img src={deviceIcon} alt="" />
                    </div>
                    <div>
                        <span className="moderator-devices__eyebrow">Управление доступом</span>
                        <h1>Рабочие устройства</h1>
                        <p>Лицензии, состояние подключения и синхронизация точек в одном месте.</p>
                    </div>
                </div>

                <label className="moderator-devices__lifetime">
                    <span>Срок нового ключа</span>
                    <select
                        value={validForDays}
                        onChange={event => setValidForDays(event.target.value)}
                    >
                        <option value={7}>7 дней</option>
                        <option value={14}>14 дней</option>
                        <option value={30}>30 дней</option>
                        <option value={90}>90 дней</option>
                        <option value={180}>180 дней</option>
                        <option value={365}>365 дней</option>
                    </select>
                </label>
            </div>

            <div className="moderator-devices__summary">
                {summaryCards.map(card => (
                    <div
                        key={card.tone}
                        className={`moderator-devices__summary-card moderator-devices__summary-card--${card.tone}`}
                    >
                        <strong>{card.value}</strong>
                        <span>{card.label}</span>
                    </div>
                ))}
            </div>

            {issuedLicense && (
                <div className="moderator-devices__issued">
                    <div className="moderator-devices__issued-copy">
                        <span className="moderator-devices__issued-mark" aria-hidden="true">✓</span>
                        <div>
                            <strong>Новый ключ для «{issuedLicense.placeName}»</strong>
                            <span>Сохраните его сейчас — после закрытия останется только префикс.</span>
                        </div>
                    </div>
                    <code>{issuedLicense.activationKey}</code>
                    <div className="moderator-devices__issued-actions">
                        <button type="button" onClick={copyActivationKey}>
                            {isCopied ? 'Скопировано' : 'Копировать'}
                        </button>
                        <button
                            type="button"
                            className="secondary"
                            onClick={() => setIssuedLicense(null)}
                        >
                            Скрыть
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div className="moderator-devices__error" role="alert">
                    <span>{error}</span>
                    <button type="button" className="secondary" onClick={load}>Повторить</button>
                </div>
            )}

            <div className="moderator-devices__content">
                <div className="moderator-devices__toolbar">
                    <label className="moderator-devices__search">
                        <span className="moderator-devices__search-icon" aria-hidden="true" />
                        <span className="sr-only">Поиск рабочих устройств</span>
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            placeholder="Название, адрес, логин или устройство"
                            autoComplete="off"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="moderator-devices__search-clear"
                                aria-label="Очистить поиск"
                                onClick={() => setSearchQuery('')}
                            >
                                ×
                            </button>
                        )}
                    </label>

                    <div className="moderator-devices__toolbar-actions">
                        <span className="moderator-devices__result-count" aria-live="polite">
                            {searchQuery
                                ? `Найдено ${visibleItems.length} из ${items.length}`
                                : `${items.length} рабочих зон`}
                        </span>
                        <button
                            type="button"
                            className="secondary moderator-devices__refresh"
                            disabled={isLoading}
                            onClick={load}
                        >
                            <span aria-hidden="true">↻</span>
                            {isLoading ? 'Обновление…' : 'Обновить'}
                        </button>
                    </div>
                </div>

                <div className="moderator-devices__table-wrap">
                    <table className="moderator-devices__table">
                        <thead>
                            <tr>
                                <th>Рабочая зона</th>
                                <th>Лицензия</th>
                                <th>Устройство</th>
                                <th>Проверка сервера</th>
                                <th>Синхронизация</th>
                                <th>Ошибки sync</th>
                                <th>Lease до</th>
                                <th><span className="sr-only">Действия</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && visibleItems.map(item => {
                                const isPending = pendingPlaceId === item.placeId;
                                const canRevoke = item.deviceId || (
                                    item.licenseId && item.licenseStatus === 'Unused');

                                return (
                                    <tr key={item.placeId}>
                                        <td className="moderator-devices__place">
                                            <strong>{item.placeName}</strong>
                                            <span>{item.address || `ID ${item.placeId}`}</span>
                                            <small className="moderator-devices__place-login">
                                                <span aria-hidden="true">@</span>
                                                {item.placeLogin || 'логин не указан'}
                                            </small>
                                        </td>
                                        <td>
                                            <span className={`moderator-devices__status ${item.licenseStatus}`}>
                                                {statusLabels[item.licenseStatus] || item.licenseStatus}
                                            </span>
                                            <small>
                                                {item.licenseKeyPrefix
                                                    ? `${item.licenseKeyPrefix}… · до ${formatDate(item.licenseExpiresAtUtc)}`
                                                    : 'Ключ не выпускался'}
                                            </small>
                                        </td>
                                        <td className="moderator-devices__device">
                                            <strong>{item.deviceName || '—'}</strong>
                                            <span>
                                                {item.deviceId
                                                    ? `${item.appVersion || 'версия неизвестна'} · ${item.operatingSystem || 'ОС неизвестна'}`
                                                    : 'Не активировано'}
                                            </span>
                                            {item.deviceId && (
                                                <React.Fragment>
                                                    <small>Активация: {formatDate(item.activatedAtUtc)}</small>
                                                    <small className="moderator-devices__device-id" title={item.deviceId}>
                                                        ID: {item.deviceId}
                                                    </small>
                                                </React.Fragment>
                                            )}
                                        </td>
                                        <td>{formatDate(item.lastServerCheckAtUtc)}</td>
                                        <td>{formatDate(item.lastSyncAtUtc)}</td>
                                        <td>
                                            {item.failedSyncOperations > 0 ? (
                                                <span
                                                    className="moderator-devices__sync-error"
                                                    title={item.lastSyncErrorMessage || item.lastSyncErrorCode}
                                                >
                                                    {item.failedSyncOperations} · {item.lastSyncErrorCode || 'ошибка'}
                                                    <small>{formatDate(item.lastSyncErrorAtUtc)}</small>
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className={isExpired(item.leaseExpiresAtUtc)
                                            ? 'moderator-devices__lease-expired'
                                            : ''}
                                        >
                                            {formatDate(item.leaseExpiresAtUtc)}
                                            {isExpired(item.leaseExpiresAtUtc) && <small>Нужен интернет</small>}
                                        </td>
                                        <td className="moderator-devices__actions">
                                            {item.licenseStatus === 'Activated' ? (
                                                <button
                                                    type="button"
                                                    disabled={isPending}
                                                    onClick={() => issue(item, true)}
                                                >
                                                    {isPending ? 'Выполняется…' : 'Заменить устройство'}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled={isPending || item.licenseStatus === 'Unused'}
                                                    onClick={() => issue(item, false)}
                                                >
                                                    {isPending ? 'Выполняется…' : 'Выпустить ключ'}
                                                </button>
                                            )}
                                            {canRevoke && (
                                                <button
                                                    type="button"
                                                    className="danger"
                                                    disabled={isPending}
                                                    onClick={() => revoke(item)}
                                                >
                                                    Отозвать
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {isLoading && (
                                <tr>
                                    <td colSpan="8" className="moderator-devices__empty">
                                        <span className="moderator-devices__spinner" aria-hidden="true" />
                                        <strong>Загружаем рабочие устройства</strong>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && items.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="moderator-devices__empty">
                                        <strong>Рабочих зон пока нет</strong>
                                        <span>Они появятся здесь после добавления в систему.</span>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && items.length > 0 && visibleItems.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="moderator-devices__empty">
                                        <strong>Ничего не найдено</strong>
                                        <span>Проверьте запрос или попробуйте другое название, адрес либо логин.</span>
                                        <button type="button" className="secondary" onClick={() => setSearchQuery('')}>
                                            Сбросить поиск
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default DeviceLicenses;
